import type { Page, ChildPage, Block } from "../../shared/schema";

// Pure helpers for reading/updating the one-level page tree (top-level pages, each optionally
// holding child/sub-pages). Kept separate from usePortalSchema so that hook only wires these
// operations to React state, rather than also owning the tree-traversal logic itself.

// Finds a page or one of its children by id, and reports the id of the parent (if any)
export function findPageOrChild(pages: Page[], id: string): { page: Page | ChildPage; parentId: string | null } | null {
  for (const page of pages) {
    if (page.id === id) return { page, parentId: null };
    const child = page.pages.find((c) => c.id === id);
    if (child) return { page: child, parentId: page.id };
  }
  return null;
}

export function addChildPageToTree(pages: Page[], parentId: string, newChild: ChildPage): Page[] {
  return pages.map((page) => (page.id === parentId ? { ...page, pages: [...page.pages, newChild] } : page));
}

export function updateChildPageInTree(
  pages: Page[],
  parentId: string,
  childId: string,
  updates: Partial<Omit<ChildPage, "id">>
): Page[] {
  return pages.map((page) =>
    page.id === parentId
      ? { ...page, pages: page.pages.map((child) => (child.id === childId ? { ...child, ...updates } : child)) }
      : page
  );
}

export function removeChildPageFromTree(pages: Page[], parentId: string, childId: string): Page[] {
  return pages.map((page) =>
    page.id === parentId ? { ...page, pages: page.pages.filter((child) => child.id !== childId) } : page
  );
}

export function reorderChildPagesInTree(pages: Page[], parentId: string, childIds: string[]): Page[] {
  return pages.map((page) => {
    if (page.id !== parentId) return page;

    const childMap = new Map(page.pages.map((c) => [c.id, c]));
    const reorderedChildren: ChildPage[] = [];
    for (const id of childIds) {
      const child = childMap.get(id);
      if (child !== undefined) reorderedChildren.push(child);
    }

    return { ...page, pages: reorderedChildren };
  });
}

// Block operations address a page by id, which may be a top-level page or a nested child page.
// Applies fn to the matching page's own blocks, leaving everything else in the tree untouched.
export function mapBlocksById(pages: Page[], pageId: string, fn: (blocks: Block[]) => Block[]): Page[] {
  return pages.map((page) => {
    if (page.id === pageId) return { ...page, blocks: fn(page.blocks) };
    const childIndex = page.pages.findIndex((c) => c.id === pageId);
    if (childIndex === -1) return page;
    const children = [...page.pages];
    const child = children[childIndex];
    if (!child) return page;
    children[childIndex] = { ...child, blocks: fn(child.blocks) };
    return { ...page, pages: children };
  });
}
